import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { suiteExecutionsGridConfig } from '@/features/executions';
import { formatExecutionDuration } from '@/features/executions/formatters/executionFormatters';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { resolveEntityTags } from '@/features/shared/tags';
import { uiTokens } from '@/theme/uiTokens';
import {
  Box,
  formatStatusLabel,
  getStatusTextColor,
  getStatusToneTextColor,
  Text,
  Tooltip,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import {
  ProjectCell,
  RunLinkCell,
  StackedCountBar,
  TableTextCell,
  TagsInlineCell,
} from '@/ui/composites/table';
import { resolveIteratorLabel } from '@/ui/composites/table/cellContents/shared';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { type GridColDef, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { QueryResult, SuiteExecutionDto } from '@flowqa/shared';

type SuiteExecutionsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
  initialData?: QueryResult<SuiteExecutionDto>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: SuiteExecutionDto[]) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showSuiteColumn?: boolean;
  showExecutionRunColumn?: boolean;
  tokenOverride?: string | null;
  disableLinks?: boolean;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
};

const columnFlex = {
  run: 0.5,
  name: 1,
  status: 0.5,
  duration: 0.5,
  tests: 1,
  executed: 0.5,
  executionRun: 1,
  tags: 0.75,
};

type StatusSegment = { label: string; value: number; color: string };
const NUMERIC_TEXT_SX = {};
const RELATIVE_TIME_TEXT_SX = {
  display: 'block',
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const mapChipKeyToStatusTone = (
  key: ReturnType<typeof getThresholdChipKey>,
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  if (key === 'yellow') return 'orange';
  if (key === null) return 'neutral';
  return key;
};

const SuiteExecutionsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting,
  noRowsText = 'No suite executions found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  initialData,
  queryKey,
  onRowsChange,
  onDataUpdatedAt,
  showLastUpdated,
  showSuiteColumn = true,
  showExecutionRunColumn = true,
  tokenOverride,
  disableLinks = false,
  sx,
  containerSx,
}: SuiteExecutionsTableProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const durationThreshold = thresholds.suites.avgDuration;
  const defaultColumns: Array<GridColDef<SuiteExecutionDto>> = useMemo(() => {
    const stringOperators = getStringFilterOperators();

    const nameColumn: GridColDef<SuiteExecutionDto> = {
      field: 'suiteName',
      headerName: 'Suite',
      flex: columnFlex.name,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_value, row) => row.name ?? row.suiteName ?? row.suiteId,
      renderCell: (params) => (
        <TableTextCell
          primary={{
            label: params.row.suiteName ?? params.row.name ?? params.row.suiteId,
            ...(disableLinks
              ? {}
              : {
                  href: `/suites/${params.row.suiteSlug ?? params.row.suiteId}`,
                  onClick: () => {
                    setSelectedProjectId(params.row.projectId);
                  },
                }),
          }}
        />
      ),
    };
    nameColumn.filterOperators = stringOperators;

    const runColumn: GridColDef<SuiteExecutionDto> = {
      field: 'runNumber',
      headerName: 'Run',
      flex: columnFlex.run,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      sortable: false,
      renderCell: (params): ReactElement => {
        const runNumber = params.row.runNumber;
        const executionLabel =
          typeof runNumber === 'number' ? resolveIteratorLabel(runNumber) : 'View';
        const href =
          !disableLinks && params.row.runNumber && (params.row.suiteSlug ?? params.row.suiteId)
            ? `/suites/${params.row.suiteSlug ?? params.row.suiteId}/runs/${params.row.runNumber}`
            : !disableLinks
              ? '/suites'
              : null;
        const isLatest = params.row.isLatest === true;
        return (
          <RunLinkCell
            label={executionLabel}
            href={href}
            latest={isLatest}
            onClick={
              disableLinks
                ? undefined
                : () => {
                    setSelectedProjectId(params.row.projectId);
                  }
            }
          />
        );
      },
    };

    const executionColumn: GridColDef<SuiteExecutionDto> = {
      field: 'status',
      headerName: 'Status',
      flex: columnFlex.status,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        const status = params.row.status;
        const label = formatStatusLabel(status);
        return (
          <Text
            role="body"
            component="span"
            sx={{
              color: (theme) => getStatusTextColor(theme, status),
            }}
            transform="uppercase"
          >
            {label.length > 0 ? label : '-'}
          </Text>
        );
      },
    };

    const durationColumn: GridColDef<SuiteExecutionDto> = {
      field: 'durationMs',
      headerName: 'Duration',
      flex: columnFlex.duration,
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        const durationMs = params.row.durationMs ?? null;
        const hasDuration = durationMs !== null;
        const durationSeconds = hasDuration ? durationMs / 1000 : null;
        const chipKey = hasDuration
          ? getThresholdChipKey(durationSeconds, durationThreshold)
          : null;
        const tone = mapChipKeyToStatusTone(chipKey);
        return (
          <Text
            role="body"
            component="span"
            sx={[
              NUMERIC_TEXT_SX,
              {
                color: hasDuration
                  ? chipKey === null
                    ? 'text.primary'
                    : (theme) => getStatusToneTextColor(theme, tone)
                  : 'textSecondary',
              },
            ]}
          >
            {formatExecutionDuration(durationMs)}
          </Text>
        );
      },
    };

    const testsColumn: GridColDef<SuiteExecutionDto> = {
      field: 'tests',
      headerName: 'Tests',
      flex: columnFlex.tests,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const total = params.row.tests ?? 0;
        const failures = params.row.failures ?? 0;
        const errors = params.row.errors ?? 0;
        const skipped = params.row.skipped ?? 0;
        const passed = Math.max(total - failures - errors - skipped, 0);
        const segments: StatusSegment[] = [
          { label: 'Passed', value: passed, color: uiTokens.statusColors.green },
          { label: 'Failed', value: failures, color: uiTokens.statusColors.red },
          { label: 'Errored', value: errors, color: uiTokens.statusColors.brown },
          { label: 'Skipped', value: skipped, color: uiTokens.statusColors.orange },
        ];
        return <StackedCountBar segments={segments} total={total} totalLabel={`Tests: ${total}`} />;
      },
    };

    const executedColumn: GridColDef<SuiteExecutionDto> = {
      field: 'timestamp',
      headerName: 'Executed',
      flex: columnFlex.executed,
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      renderCell: (params) => {
        const label = formatRelativeTime(params.row.timestamp);
        const fullTimestamp = formatDate(params.row.timestamp, { includeTime: true });
        return (
          <Tooltip
            title={fullTimestamp}
            disableHoverListener={!fullTimestamp}
            arrow
            slotProps={{
              tooltip: { sx: { fontSize: (theme) => theme.typography.body2.fontSize } },
            }}
          >
            <Box component="span" sx={{ display: 'block', width: '100%' }} title={label}>
              <Text role="body" component="span" sx={RELATIVE_TIME_TEXT_SX}>
                {label.length > 0 ? label : '-'}
              </Text>
            </Box>
          </Tooltip>
        );
      },
    };

    const executionRunColumn: GridColDef<SuiteExecutionDto> = {
      field: 'runId',
      headerName: 'Execution Run',
      flex: columnFlex.executionRun,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      sortable: true,
      renderCell: (params): ReactElement => (
        <ProjectCell label={params.row.runId} href={`/execution-runs/${params.row.runId}`} />
      ),
    };

    const tagsColumn: GridColDef<SuiteExecutionDto> = {
      field: 'tags',
      headerName: 'Tags',
      flex: columnFlex.tags,
      filterable: false,
      sortable: false,
      renderCell: (params): ReactElement => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
    };

    const orderedColumns: Array<GridColDef<SuiteExecutionDto>> = [
      executionColumn,
      testsColumn,
      durationColumn,
      executedColumn,
    ];
    const baseColumns: Array<GridColDef<SuiteExecutionDto>> = showSuiteColumn
      ? [runColumn, nameColumn, ...orderedColumns]
      : [runColumn, ...orderedColumns];
    const columns = showExecutionRunColumn
      ? [...baseColumns, executionRunColumn, tagsColumn]
      : [...baseColumns, tagsColumn];

    return columns;
  }, [
    disableLinks,
    durationThreshold,
    setSelectedProjectId,
    showExecutionRunColumn,
    showSuiteColumn,
  ]);

  const gridConfig = suiteExecutionsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'suite-executions-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });

  const gridProps = buildTableGridProps<SuiteExecutionDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onDataUpdatedAt,
    showLastUpdated,
    chrome: 'embedded',
  });

  return (
    <ServerDataTable<SuiteExecutionDto>
      queryPath="suite-executions/query"
      {...gridConfig}
      {...gridProps}
      initialData={initialData}
      columns={defaultColumns}
      getRowId={(row) => row.id}
      token={resolvedToken}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      disableColumnSorting={disableColumnSorting}
      sx={sx}
      containerSx={containerSx}
    />
  );
};

export const SuiteExecutionsTable = memo(SuiteExecutionsTableComponent);
